import { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, isTouchDevice, Modal, TextField } from "@shapr3d/frontend-lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { queryKeys } from "../../config";
import { accountQueryKey } from "../../hooks/useAccount";
import type { AccountResponseData } from "../../hooks/useAccountTypes";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { UMSrequestClient } from "../../stores/requestClient";

import style from "./TeamNameChangeModal.module.css";

type TeamNameChangeFormData = { name: string };

export default function TeamNameChangeModal({
  open = true,
  onClose,
  name,
}: {
  open?: boolean;
  onClose: () => void;
  name?: string;
}) {
  const { t, m } = useTranslation();
  const schema = useMemo(
    () =>
      z.object({
        name: z.string().nonempty(t(m.TeamSelector.PleaseEnterTeamName)),
      }),
    [t, m]
  );
  const { teamID } = useParams();
  const tracker = useTracker();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<TeamNameChangeFormData>({
    resolver: zodResolver(schema),
    defaultValues: { name },
  });

  useEffect(() => {
    if (open) {
      reset({ name });
    }
  }, [open, name, reset]);

  const queryClient = useQueryClient();

  const { mutateAsync, error } = useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "request-team-name-change"],
    mutationFn: ({ name }: { name: string }) =>
      UMSrequestClient.authenticatedRequest({
        method: "PATCH",
        url: `/teams/${teamID}`,
        body: { name },
      }),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<AccountResponseData | undefined>(accountQueryKey, (oldData) => {
        const teamData = oldData?.teams.find((team) => team.id === teamID);
        if (oldData && teamData) {
          return {
            ...oldData,
            teams: [
              ...oldData.teams.filter((team) => team.id !== teamID),
              {
                ...teamData,
                name: variables.name,
              },
            ],
          };
        }
        return oldData;
      });
    },
  });

  const submit = async (data: TeamNameChangeFormData) => {
    try {
      await mutateAsync(data);
      onClose();
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  let errorMessage = null;
  if (error) {
    errorMessage = t(m.ErrorMessage.Unknown);
  }

  return (
    <Modal open={open} title={t(m.TeamSelector.EditTeamName)} size="small" onClose={onClose}>
      <form className={style.teamNameChangeForm} onSubmit={handleSubmit(submit)}>
        <TextField
          {...register("name")}
          placeholder={t(m.TeamSelector.TeamName)}
          errorMessage={errors.name?.message}
          maxLength={200}
          disabled={isSubmitting}
          autoFocus={!isTouchDevice()}
        ></TextField>
        {errorMessage && <div className={style.error}>{errorMessage}</div>}
        <div className={style.buttonContainer}>
          <Button
            type="submit"
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.CONFIRM,
                location: ElementClickLocation.UPDATE_TEAM_NAME_MODAL,
              });
            }}
            disabled={isSubmitting}
          >
            {t(m.TeamSelector.Save)}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
